import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useGetAdminUserDetailQuery,
  useGetAdminUserHourlyPositionsQuery,
  useGetAdminUserTradesQuery,
  useGetAdminUserLlmCostQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert } from "@/components/ui/alert";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { EquityCurve } from "@/components/charts/EquityCurve";
import { DailyPnL } from "@/components/charts/DailyPnL";
import { PnLPill } from "@/components/domain/PnLPill";
import { fmtUsd, fmtNum, fmtTime, fmtRelative } from "@/lib/format";
import { ArrowLeft } from "lucide-react";

/** Admin per-user dashboard. Bundles everything an operator usually
 *  wants to see for one user on a single page: profile, settings,
 *  engine state, both equity / daily-P&L charts, open positions with
 *  running P&L, recent trades + cycles, fees, and the (global) LLM
 *  cost panel with a clear "shared across users" caveat. */
export default function AdminUserDetail() {
  const { uid: uidParam } = useParams<{ uid: string }>();
  const uid = Number(uidParam);

  const detail = useGetAdminUserDetailQuery(uid, {
    pollingInterval: 30000, skip: !uid,
  });
  const openHourly = useGetAdminUserHourlyPositionsQuery(
    { uid, status: "open" }, { pollingInterval: 15000, skip: !uid },
  );
  const closedHourly = useGetAdminUserHourlyPositionsQuery(
    { uid, status: "closed", limit: 1000 },
    { pollingInterval: 60000, skip: !uid },
  );
  const trades = useGetAdminUserTradesQuery(
    { uid, limit: 50 }, { pollingInterval: 30000, skip: !uid },
  );
  const llm7d = useGetAdminUserLlmCostQuery(
    { uid, days: 7 }, { pollingInterval: 60000, skip: !uid },
  );
  const llm1d = useGetAdminUserLlmCostQuery(
    { uid, days: 1 }, { pollingInterval: 60000, skip: !uid },
  );

  if (!Number.isFinite(uid)) {
    return <Alert tone="danger">Invalid user id.</Alert>;
  }
  if (detail.isLoading) return <Skeleton className="h-96 w-full" />;
  if (!detail.data) return <Alert tone="danger">User not found.</Alert>;

  const u = detail.data.user as { id: number; email: string; role: string; status: string; created_at: string };
  const s = (detail.data.settings ?? {}) as Record<string, unknown>;
  const fees = (detail.data.fees ?? {}) as Record<string, unknown>;
  const tradesToday = detail.data.trades_today ?? 0;

  const closedHourlyRows = closedHourly.data ?? [];
  const openHourlyRows = openHourly.data ?? [];
  const recentTrades = trades.data ?? [];

  // Aggregates
  const stats = useMemo(() => {
    const tot = (arr: any[], k: string) => arr.reduce((acc, x) => acc + Number(x?.[k] ?? 0), 0);
    return {
      lifetimeHourly: tot(closedHourlyRows, "net_pnl_usd"),
      hourlyOpen: openHourlyRows.length,
      openValueHourly: openHourlyRows.reduce((acc, p: any) =>
        acc + Number(p.size_usd ?? 0) + Number(p.dca_total_added_usd ?? 0), 0),
      live7d: recentTrades
        .filter((t) => t.action === "SELL" && typeof t.realized_pnl_usd === "number")
        .reduce((acc, t) => acc + Number(t.realized_pnl_usd), 0),
    };
  }, [closedHourlyRows, openHourlyRows, recentTrades]);

  return (
    <div className="space-y-4">
      {/* Header strip */}
      <Card>
        <CardHeader>
          <div className="flex items-start gap-3 flex-wrap">
            <Link to="/admin/users" className="text-xs text-accent inline-flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> back to users
            </Link>
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <span>#{u.id}</span>
              <span className="font-mono text-sm">{u.email}</span>
              <Badge tone={u.role === "admin" ? "info" : "muted"}>{u.role}</Badge>
              <Badge tone={u.status === "active" ? "good" : "warn"}>{u.status}</Badge>
              <Badge tone={detail.data.has_keys ? "good" : "muted"}>
                {detail.data.has_keys ? "Binance keys" : "no keys"}
              </Badge>
              <Badge tone={s.scalp_enabled ? "good" : "muted"}>
                scalp {s.scalp_enabled ? (s.scalp_paused ? "paused" : "on") : "off"}
              </Badge>
              <Badge tone={s.can_run_cycle ? "good" : "muted"}>
                cycles {s.can_run_cycle ? (s.paused ? "paused" : "on") : "off"}
              </Badge>
              {s.dry_run ? <Badge tone="warn">DRY RUN</Badge> : null}
            </CardTitle>
          </div>
          <p className="text-xs text-muted mt-1">
            joined {fmtTime(u.created_at)} ({fmtRelative(u.created_at)})
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-sm">
            <Kpi label="Trades today" value={String(tradesToday)} />
            <Kpi label="Hourly open" value={String(stats.hourlyOpen)}
                  sub={stats.hourlyOpen > 0 ? `${fmtUsd(stats.openValueHourly)} notional` : ""} />
            <Kpi label="Hourly lifetime net" value={fmtUsd(stats.lifetimeHourly)} tone={stats.lifetimeHourly >= 0 ? "good" : "bad"} />
            <Kpi label="Outstanding fee" value={fmtUsd(Number(fees.outstanding_fee ?? 0))}
                  sub={`paid ${fmtUsd(Number(fees.paid_fee ?? 0))}`} />
          </div>
        </CardContent>
      </Card>

      {/* Equity + daily P&L grid */}
      <div className="grid lg:grid-cols-2 gap-3">
        <ChartCard title="Hourly — equity curve" loading={closedHourly.isLoading}>
          <EquityCurve positions={closedHourlyRows as any} height={180} />
        </ChartCard>
        <ChartCard title="Hourly — daily P&L (14d)" loading={closedHourly.isLoading}>
          <DailyPnL positions={closedHourlyRows as any} days={14} height={180} />
        </ChartCard>
      </div>

      {/* Open hourly positions */}
      <Card>
        <CardHeader>
          <CardTitle>
            Open hourly positions
            <span className="text-xs text-muted ml-2 font-normal">
              {openHourlyRows.length} open · live unrealized P&L
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {openHourly.isLoading ? <Skeleton className="h-24 w-full" /> :
           openHourlyRows.length === 0 ? <p className="text-sm text-muted">No open hourly positions.</p> :
           <OpenHourlyTable rows={openHourlyRows} />}
        </CardContent>
      </Card>

      {/* Recent trades */}
      <Card>
        <CardHeader>
          <CardTitle>
            Recent trades
            <span className="text-xs text-muted ml-2 font-normal">
              last {recentTrades.length} · realized over closed cycles
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {trades.isLoading ? <Skeleton className="h-32 w-full" /> :
           recentTrades.length === 0 ? <p className="text-sm text-muted">No trades.</p> :
           <RecentTradesTable rows={recentTrades} />}
        </CardContent>
      </Card>

      {/* Settings snapshot */}
      <Card>
        <CardHeader>
          <CardTitle>Settings snapshot</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <SettingRow label="per_order_usd" value={s.per_order_usd} />
            <SettingRow label="max_orders_per_day" value={s.max_orders_per_day} />
            <SettingRow label="approval_mode" value={s.approval_mode} />
            <SettingRow label="paused" value={s.paused} />
            <SettingRow label="dry_run" value={s.dry_run} />
            <SettingRow label="can_run_cycle" value={s.can_run_cycle} />
            <SettingRow label="per_user_cycle_enabled" value={s.per_user_cycle_enabled} />
            <SettingRow label="starting_equity_usd" value={s.starting_equity_usd} />
          </div>
        </CardContent>
      </Card>

      {/* LLM cost — per-user */}
      <Card>
        <CardHeader>
          <CardTitle>
            Gemini / LLM cost — this user
            <span className="text-xs text-muted ml-2 font-normal">
              only per-user agents: per_user_reviewer · hourly_exit_planner ·
              per-engine advisors
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Kpi label="24h cost" value={fmtUsd(Number(llm1d.data?.total_cost_usd ?? 0))}
                  sub={`${llm1d.data?.total_calls ?? 0} calls`} />
            <Kpi label="7d cost" value={fmtUsd(Number(llm7d.data?.total_cost_usd ?? 0))}
                  sub={`${llm7d.data?.total_calls ?? 0} calls`} />
            <Kpi label="24h tokens" value={String(llm1d.data?.total_tokens ?? 0)} />
            <Kpi label="7d tokens" value={String(llm7d.data?.total_tokens ?? 0)} />
          </div>
          {(llm7d.data?.by_agent?.length ?? 0) > 0 && (
            <div className="mt-3">
              <div className="text-xs text-muted mb-1">7-day breakdown by agent</div>
              <Table>
                <THead><TR>
                  <TH>Agent</TH>
                  <TH className="text-right">Calls</TH>
                  <TH className="text-right">Tokens</TH>
                  <TH className="text-right">Cost</TH>
                  <TH className="text-right">Avg latency</TH>
                  <TH className="text-right">Errors</TH>
                </TR></THead>
                <TBody>
                  {(llm7d.data?.by_agent ?? []).map((a) => (
                    <TR key={a.agent}>
                      <TD className="font-mono text-xs">{a.agent}</TD>
                      <TD className="text-right tabular">{a.calls}</TD>
                      <TD className="text-right tabular">{a.tokens.toLocaleString()}</TD>
                      <TD className="text-right tabular">{fmtUsd(a.cost_usd)}</TD>
                      <TD className="text-right tabular">{a.avg_latency_ms}ms</TD>
                      <TD className="text-right tabular">
                        {a.errors > 0
                          ? <span className="text-bad">{a.errors}</span>
                          : <span className="text-muted">0</span>}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          )}
          <Alert tone="info" className="mt-3">
            Engine-level agents shared across users (tuners, audit bot,
            narrator, watchlist, the global hourly advisor, BTC dominance
            fetch) are tagged with NULL user_id and excluded here. See
            <code className="ml-1">/api/llm/cost-summary</code> for the
            global view.
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------- small render helpers ---------- */

function Kpi({ label, value, sub, tone }: {
  label: string; value: string; sub?: string;
  tone?: "good" | "bad" | "warn" | "muted";
}) {
  const color =
    tone === "good" ? "text-good" :
    tone === "bad" ? "text-bad" :
    tone === "warn" ? "text-warn" : "text-fg";
  return (
    <div className="border border-border rounded-md p-2 bg-bg2/30">
      <div className="text-xs text-muted uppercase tracking-wide">{label}</div>
      <div className={`text-lg font-semibold tabular ${color}`}>{value}</div>
      {sub && <div className="text-xs text-muted">{sub}</div>}
    </div>
  );
}

function SettingRow({ label, value }: { label: string; value: unknown }) {
  let display: string;
  if (typeof value === "boolean") display = value ? "true" : "false";
  else if (value === null || value === undefined) display = "—";
  else display = String(value);
  return (
    <div className="border border-border rounded-md p-2 bg-bg2/30">
      <div className="text-muted">{label}</div>
      <div className="font-mono">{display}</div>
    </div>
  );
}

function ChartCard({ title, loading, children }: {
  title: string; loading: boolean; children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? <Skeleton className="h-44 w-full" /> : children}
      </CardContent>
    </Card>
  );
}

function OpenHourlyTable({ rows }: { rows: any[] }) {
  return (
    <Table>
      <THead><TR>
        <TH>Symbol</TH>
        <TH className="text-right">Entry</TH>
        <TH className="text-right">Last</TH>
        <TH className="text-right">P&amp;L</TH>
        <TH className="text-right">Target</TH>
        <TH className="text-right">Stop</TH>
        <TH className="text-right">Size</TH>
        <TH>Opened</TH>
      </TR></THead>
      <TBody>
        {rows.map((p) => (
          <TR key={p.id}>
            <TD className="font-medium">{p.symbol}</TD>
            <TD className="text-right tabular">{Number(p.entry_price).toFixed(6)}</TD>
            <TD className="text-right tabular">
              {p.current_price != null ? Number(p.current_price).toFixed(6) : "—"}
              {p.pct_change != null && (
                <div className={"text-xs " + (p.pct_change >= 0 ? "text-good" : "text-bad")}>
                  {p.pct_change >= 0 ? "+" : ""}{(p.pct_change * 100).toFixed(2)}%
                </div>
              )}
            </TD>
            <TD className="text-right"><PnLPill value={p.unrealized_net_usd ?? null} /></TD>
            <TD className="text-right tabular">{Number(p.target_price).toFixed(6)}</TD>
            <TD className="text-right tabular">{Number(p.stop_price).toFixed(6)}</TD>
            <TD className="text-right tabular">
              {fmtUsd(Number(p.size_usd) + Number(p.dca_total_added_usd ?? 0))}
            </TD>
            <TD className="text-xs text-muted">{fmtTime(p.entry_time)}</TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}


function RecentTradesTable({ rows }: { rows: any[] }) {
  return (
    <Table>
      <THead><TR>
        <TH>Time</TH>
        <TH>Side</TH>
        <TH>Source</TH>
        <TH>Symbol</TH>
        <TH className="text-right">Size USD</TH>
        <TH className="text-right">Filled</TH>
        <TH className="text-right">Avg price</TH>
        <TH className="text-right">Fee</TH>
        <TH className="text-right">Realized P&amp;L</TH>
        <TH>Mode</TH>
      </TR></THead>
      <TBody>
        {rows.map((t) => (
          <TR key={t.id}>
            <TD className="text-xs text-muted">{fmtTime(t.executed_at)}</TD>
            <TD><Badge tone={t.action === "BUY" ? "good" : "bad"}>{t.action}</Badge></TD>
            <TD>
              <Badge tone={t.source === "scalp" ? "info" : "default"}>
                {t.source ?? "hourly"}
              </Badge>
            </TD>
            <TD>{t.symbol}</TD>
            <TD className="text-right tabular">{fmtUsd(t.size_usd ?? null)}</TD>
            <TD className="text-right tabular">{fmtNum(t.filled_qty ?? null, 6)}</TD>
            <TD className="text-right tabular">{fmtNum(t.avg_price ?? null, 6)}</TD>
            <TD className="text-right tabular">
              {fmtUsd(t.fee ?? null)}{" "}
              <span className="text-muted text-xs">{t.fee_currency ?? ""}</span>
            </TD>
            <TD className="text-right">
              {t.action === "SELL" && t.realized_pnl_usd != null ? (
                <PnLPill value={t.realized_pnl_usd} />
              ) : <span className="text-xs text-muted">—</span>}
            </TD>
            <TD><Badge tone={t.dry_run ? "warn" : "bad"}>{t.dry_run ? "dry" : "live"}</Badge></TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}
